import type {
  BildschirmPhase,
  Channel,
  GewinnspielBefehl,
  GewinnspielZustand,
  Message,
  RafflePrize,
  Session,
  Showcase,
  TourStop,
} from '@k4ia/shared'
import { STRINGS } from './i18n-strings'
import { useChatStore } from '../state/chatStore'

async function jsonFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, { credentials: 'include', ...init })
  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    throw new Error(
      (body as { error?: string; message?: string }).error ??
        (body as { message?: string }).message ??
        `HTTP ${res.status}`,
    )
  }
  return res.json() as Promise<T>
}

/** Was der Worker über das vorige Gespräch sagt (routes/session.ts). */
export interface SessionStart {
  id: string
  channel: Channel
  /** Dasselbe Gespräch läuft weiter; wann die letzte Nachricht war. */
  fortgesetzt: { letzteNachrichtAm: string | null } | null
  /** Ein neues Gespräch, weil das alte abgelaufen war oder der Besucher neu anfangen wollte. */
  neuBegonnen: { letzteNachrichtAm: string | null; grund: 'timeout' | 'neu' } | null
  /** Ab so vielen Minuten Pause fragt der Chat „weitermachen oder neu anfangen?“ (Admin). */
  hinweisAbMinuten: number
}

export function initSession(params: {
  channel: Channel
  locale?: string
  addressForm?: 'du' | 'sie'
  /** „Neu anfangen“: das bisherige Gespräch verlassen. */
  neu?: boolean
}) {
  return jsonFetch<SessionStart>('/api/v1/session', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(params),
  })
}

export function fetchSessionState() {
  return jsonFetch<{ session: Session | null; messages: Message[] }>('/api/v1/session')
}

export function fetchShowcases() {
  return jsonFetch<{ showcases: Showcase[] }>('/api/v1/showcases')
}

/** Der geführte Rundgang (Admin „Rundgang"): Stationen in Reihenfolge, mit Wegbeschreibung. */
export function fetchTour() {
  return jsonFetch<{ stops: TourStop[] }>('/api/v1/tour')
}

export function fetchShowcase(slug: string) {
  return jsonFetch<{
    showcase: Showcase
    /** Tag-Bildschirm: „Vorlesen“ anbieten? (Admin → Konfiguration → Gespräch.) Fehlt bei alten Workern. */
    einstieg?: { vorlesen: boolean }
  }>(`/api/v1/showcases/${slug}`)
}

export type ChatEntry = 'tag' | 'tour-walk' | 'tour-arrived' | 'tour-end'

export function postChatMessage(
  body: {
    message: string
    showcaseContextId?: string
    imageDataUrl?: string
    /** Mehrere Showcases im Kamerabild (ganze Vitrine). */
    showcaseContextIds?: string[]
    /** NFC-Tag gescannt bzw. Zug im geführten Rundgang — siehe chatRequestSchema.entry. */
    entry?: ChatEntry
  },
  signal?: AbortSignal,
): Promise<Response> {
  return fetch('/api/v1/chat', {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  })
}

export function postToolConfirmation(
  toolCallId: string,
  confirmed: boolean,
  input?: Record<string, unknown>,
  signal?: AbortSignal,
): Promise<Response> {
  return fetch('/api/v1/chat/confirm', {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(input ? { toolCallId, confirmed, input } : { toolCallId, confirmed }),
    signal,
  })
}

export interface VisitSummary {
  startedAt: string
  channel: string
  showcases: { id: string; slug: string; title: string; shortText: string; keyMessages: string[] }[]
  questionCount: number
  questions: string[]
}

/** SPEC "Besuchs-Zusammenfassung" — public, needs no session (opened from a mailed link). */
export function fetchVisit(token: string) {
  return jsonFetch<{ visit: VisitSummary }>(`/api/v1/visit/${encodeURIComponent(token)}`)
}

/** "Wiedererkennung per Link": re-attaches this browser to the original session. */
export function continueVisit(token: string) {
  return jsonFetch<{ ok: boolean; sessionId: string }>(
    `/api/v1/visit/${encodeURIComponent(token)}/continue`,
    { method: 'POST' },
  )
}

/** SPEC "Show-Modus": tablet and big screen join the same session via a pairing code. */
export function joinShowSession(code: string) {
  return jsonFetch<{ ok: boolean; sessionId: string }>(
    `/api/v1/show/${encodeURIComponent(code)}/join`,
    { method: 'POST' },
  )
}

/** SPEC "Analytics: … Bewertungen" — a thumbs up/down on one answer. */
export function postFeedback(messageId: string, rating: 'up' | 'down', comment?: string) {
  return jsonFetch<{ ok?: boolean }>('/api/v1/feedback', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ messageId, rating, comment }),
  })
}

/** ADR 0004: server issues a short-lived signed ElevenLabs WS URL — the raw API key never reaches the browser. */
/**
 * Holt die signierte Gesprächsadresse und das Sprach-Token.
 *
 * Beides hängt an einer bestehenden Sitzung — und die kann fehlen: nach einem
 * Neuladen, in einem privaten Fenster, oder wenn jemand schneller auf
 * „Sprechen starten" klickt, als das Sitzungs-Cookie gesetzt ist. Dann legt
 * dieser Aufruf die Sitzung an und versucht es **einmal** erneut, statt den
 * Besucher mit „bitte zuerst /api/v1/session aufrufen" stehen zu lassen — ein
 * Satz, der ihm nichts sagt und den er nicht befolgen kann.
 *
 * Genau einmal: Scheitert es auch dann, liegt es nicht an der Sitzung, und
 * eine Schleife würde den Fehler nur verdecken.
 */
export async function fetchVoiceToken(channel: Channel = 'web') {
  try {
    return await voiceTokenFetch()
  } catch (err) {
    if (!/Keine Sitzung/i.test(err instanceof Error ? err.message : '')) throw err
    await initSession({ channel })
    return voiceTokenFetch()
  }
}

/**
 * Sind alle gleichzeitigen Sprachgespräche des ElevenLabs-Tarifs belegt, sagt
 * der Worker das mit `code: voice_busy`. Dann ist nichts kaputt — und der
 * Besucher bekommt einen Satz in seiner Sprache, der ihn in den Chat schickt,
 * statt der technischen Meldung für das Team.
 */
async function voiceTokenFetch() {
  const res = await fetch('/api/v1/voice/token', { method: 'POST', credentials: 'include' })
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string; code?: string }
    if (body.code === 'voice_busy') {
      throw new Error(STRINGS[useChatStore.getState().locale].voiceBusy)
    }
    throw new Error(body.error ?? `HTTP ${res.status}`)
  }
  return res.json() as Promise<{ signedUrl: string; voiceToken: string }>
}

/** Ein Befehl vom Handy an den Intro-Bildschirm. `thema` ist die ID eines Showcases. */
export type IntroBefehl =
  | { art: 'thema'; thema: string }
  | { art: 'pause' }
  | { art: 'weiter' }
  | { art: 'stopp' }
  | { art: 'chat' }
  | { art: 'boxen' }
  | { art: 'ende' }

/** Was der Bildschirm meldet: Phase, Box, ob K4ia gerade spricht und ob pausiert. */
export interface IntroBildschirm {
  phase: BildschirmPhase
  thema: string | null
  spricht: boolean
  pausiert: boolean
}

/**
 * Welche Bühne diese Seite bedient: `/intro` im Lab oder `/schaufenster`.
 * Beide haben dieselben Routen unter `/api/v1/<name>`. Der Bildschirm im
 * Schaufenster weist sich zusätzlich mit seinem Schlüssel aus (`k`, aus der
 * Adresse im Admin). Einmal je Seite gesetzt, beim Einhängen.
 */
let buehne: { basis: string; k: string } = { basis: '/api/v1/intro', k: '' }

export function buehneWaehlen(name: 'intro' | 'schaufenster', schluessel = ''): void {
  buehne = { basis: `/api/v1/${name}`, k: schluessel }
}

/** Hängt den Schlüssel des Bildschirms an (nur Schaufenster). */
function mitSchluessel(pfad: string): string {
  if (!buehne.k) return pfad
  return `${pfad}${pfad.includes('?') ? '&' : '?'}k=${encodeURIComponent(buehne.k)}`
}

/** `/intro`: was der Bildschirm gerade tut, wie es das Handy sieht. */
export interface IntroZustand {
  zaehler: number
  befehl: IntroBefehl & { nr: number }
  bildschirm: IntroBildschirm
  /** Hat sich der Bildschirm in den letzten Sekunden gemeldet? Vom Worker berechnet. */
  verbunden: boolean
  /** Schaufenster: Steuert gerade ein Handy? */
  belegt?: boolean
  /** Die Sitzung des Chats, den das Handy führt; `null` = kein Chat. */
  chatSitzung?: string | null
}

export function fetchIntroState() {
  return jsonFetch<IntroZustand>(`${buehne.basis}/state`, { cache: 'no-store' })
}

/** Vom Bildschirm, jede Sekunde: meldet seinen Stand, bekommt Zähler und letzten Befehl zurück. */
export function reportIntroScreen(meldung: IntroBildschirm) {
  return jsonFetch<IntroZustand>(mitSchluessel(`${buehne.basis}/bildschirm`), {
    method: 'POST',
    cache: 'no-store',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(meldung),
  })
}

/** Vom Handy aus, mit dem Knopf „Intro starten"; das Token stammt aus der URL auf dem Tag. */
export function triggerIntro(token: string) {
  return jsonFetch<{ gezaehlt: boolean }>(`${buehne.basis}/trigger/${encodeURIComponent(token)}`, {
    method: 'POST',
  })
}

/** Vom Handy aus: Box, Pause/Weiter/Stopp, zurück zu den Boxen oder beenden. */
export function sendIntroCommand(token: string, befehl: Exclude<IntroBefehl, { art: 'chat' }>) {
  return jsonFetch<IntroZustand>(`${buehne.basis}/befehl/${encodeURIComponent(token)}`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(befehl),
  })
}

/**
 * Vom Handy aus: den Chat öffnen. Das Handy bekommt dabei kein Sitzungs-Cookie
 * — sein eigenes Gespräch bleibt, wo es war — und fragt über
 * `sendIntroChatMessage`.
 */
export function openIntroChat(token: string) {
  return jsonFetch<IntroZustand>(`${buehne.basis}/chat/${encodeURIComponent(token)}`, {
    method: 'POST',
  })
}

/** Vom Handy aus: eine Frage in den Intro-Chat; die Antwort kommt als Stream (siehe lib/sse.ts). */
export function sendIntroChatMessage(
  token: string,
  message: string,
  kontext: IntroKamerakontext = {},
  signal?: AbortSignal,
) {
  return fetch(`${buehne.basis}/chat/${encodeURIComponent(token)}/nachricht`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ message, ...kontext }),
    signal,
  })
}

/** Was die Kamera am Handy zur Frage beisteuert — wie `showcaseContextId(s)`/`imageDataUrl` im gewöhnlichen Chat. */
export interface IntroKamerakontext {
  showcaseId?: string
  showcaseIds?: string[]
  imageDataUrl?: string
}

export interface IntroErkennung {
  showcaseId: string | null
  confidence: number
  alternatives: { showcaseId: string; confidence: number }[]
}

/**
 * Vom Handy aus: Welcher Showcase ist auf dem Bild? Gründlich (zweite Stufe
 * mit dem Sprachmodell, wenn das kleine nichts sieht). Kein Bild wird gespeichert.
 */
export async function recognizeIntro(
  token: string,
  imageDataUrl: string,
  optionen: { source: 'button' | 'photo'; stage2?: 'fast' | 'strong'; timeoutMs?: number },
): Promise<IntroErkennung> {
  const res = await fetch(`${buehne.basis}/vision/${encodeURIComponent(token)}`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      imageDataUrl,
      thorough: true,
      source: optionen.source,
      ...(optionen.stage2 ? { stage2: optionen.stage2 } : {}),
    }),
    signal: AbortSignal.timeout(optionen.timeoutMs ?? 25_000),
  })
  const body = (await res.json().catch(() => ({}))) as Partial<IntroErkennung> & { error?: string }
  if (!res.ok) throw new Error(body.error ?? `HTTP ${res.status}`)
  return {
    showcaseId: body.showcaseId ?? null,
    confidence: body.confidence ?? 0,
    alternatives: body.alternatives ?? [],
  }
}

/** Vom Handy aus: eine Aufnahme in Text (ElevenLabs Speech-to-Text, nichts wird gespeichert). */
export async function transcribeIntro(token: string, aufnahme: Blob): Promise<string> {
  const res = await fetch(`${buehne.basis}/stt/${encodeURIComponent(token)}`, {
    method: 'POST',
    headers: { 'content-type': aufnahme.type || 'audio/webm' },
    body: aufnahme,
  })
  const body = (await res.json().catch(() => ({}))) as {
    text?: string
    error?: string
    hinweis?: string
  }
  // Mit Hinweis: „HTTP 401" allein sagt nicht, dass nur eine Berechtigung fehlt.
  if (!res.ok) {
    throw new Error([body.error ?? `HTTP ${res.status}`, body.hinweis].filter(Boolean).join(' '))
  }
  return body.text ?? ''
}

export interface IntroChatNachricht {
  id: string
  role: 'user' | 'assistant'
  content: string
  /** Adresse des Fotos, das mit dieser Frage vom Handy kam (nur solange der Chat läuft). */
  foto?: string
  /** „powered by": Die Antwort gehört zu einem Showcase mit Partner. */
  partner?: { partnerName: string; partnerLogoKey: string | null }
}

/** Vom Bildschirm: die letzten Nachrichten des Chats (im Schaufenster nur K4ias Antworten). */
export async function fetchIntroChatHistory() {
  const r = await jsonFetch<{ nachrichten: IntroChatNachricht[] }>(
    mitSchluessel(`${buehne.basis}/chat/verlauf`),
    { cache: 'no-store' },
  )
  return {
    nachrichten: r.nachrichten.map((n) => (n.foto ? { ...n, foto: mitSchluessel(n.foto) } : n)),
  }
}

/** Vom Handy: das ganze Gespräch, auch die eigenen Fragen. */
export async function fetchIntroChatHistoryHandy(token: string) {
  const r = await jsonFetch<{ nachrichten: IntroChatNachricht[] }>(
    `${buehne.basis}/chat/${encodeURIComponent(token)}/verlauf`,
    { cache: 'no-store' },
  )
  const t = `t=${encodeURIComponent(token)}`
  return {
    nachrichten: r.nachrichten.map((n) =>
      n.foto ? { ...n, foto: `${n.foto}${n.foto.includes('?') ? '&' : '?'}${t}` } : n,
    ),
  }
}

/** Schaufenster, vom Bildschirm: der QR-Code gerade jetzt und wann er wechselt. */
export function fetchSchaufensterQr() {
  return jsonFetch<{ code: string; url: string; wechseltInMs: number }>(
    mitSchluessel('/api/v1/schaufenster/qr'),
    { cache: 'no-store' },
  )
}

export type SchaufensterNehmen =
  { art: 'ok'; token: string } | { art: 'belegt'; freiInSek: number } | { art: 'abgelaufen' }

/** Schaufenster, vom Handy nach dem Scan: die Steuerung übernehmen. */
export async function nehmeSchaufenster(code: string): Promise<SchaufensterNehmen> {
  const res = await fetch(`/api/v1/schaufenster/nehmen/${encodeURIComponent(code)}`, {
    method: 'POST',
  })
  const body = (await res.json().catch(() => ({}))) as { token?: string; freiInSek?: number }
  if (res.ok && body.token) return { art: 'ok', token: body.token }
  if (res.status === 409) return { art: 'belegt', freiInSek: body.freiInSek ?? 60 }
  if (res.status === 404) return { art: 'abgelaufen' }
  throw new Error(`HTTP ${res.status}`)
}

/** Schaufenster, vom Handy: „Fenster freigeben“. */
export function gebeSchaufensterAb(token: string) {
  return jsonFetch<{ ok: boolean }>(`/api/v1/schaufenster/abgeben/${encodeURIComponent(token)}`, {
    method: 'POST',
  })
}

export interface IntroSpeech {
  etag: string
  audio: ArrayBuffer
}

/**
 * Die gesprochene Fassung des Intro-Textes (`null`), eines Showcases oder einer
 * Antwort aus dem Intro-Chat. Mit der vorigen Fassung als `bekannt` fragt der
 * Bildschirm nur nach, ob sie noch gilt (304), statt sie jedes Mal neu zu laden.
 */
export type IntroSprachQuelle = { showcaseId: string } | { nachricht: string } | null

/**
 * Wo die Tonspur liegt. Der Bildschirm gibt diese Adresse direkt einem
 * Audio-Element: Das spielt ab, sobald die ersten Takte da sind, statt auf die
 * ganze Datei zu warten.
 */
export function introSpeechUrl(quelle: IntroSprachQuelle): string {
  return mitSchluessel(
    quelle === null
      ? `${buehne.basis}/speech`
      : 'showcaseId' in quelle
        ? `${buehne.basis}/speech?showcase=${encodeURIComponent(quelle.showcaseId)}`
        : `${buehne.basis}/chat/sprache?nachricht=${encodeURIComponent(quelle.nachricht)}`,
  )
}

export async function fetchIntroSpeech(
  quelle: IntroSprachQuelle,
  bekannt?: IntroSpeech | null,
): Promise<IntroSpeech> {
  const res = await fetch(introSpeechUrl(quelle), {
    credentials: 'include',
    cache: 'no-store',
    headers: bekannt ? { 'if-none-match': bekannt.etag } : {},
  })
  if (res.status === 304 && bekannt) return bekannt
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string; hinweis?: string }
    // Der Hinweis sagt, was zu tun ist — am Bildschirm steht sonst nur der Status.
    throw new Error([body.error ?? `HTTP ${res.status}`, body.hinweis].filter(Boolean).join(' '))
  }
  return { etag: res.headers.get('etag') ?? '', audio: await res.arrayBuffer() }
}

/**
 * Sprachmodus: War der kurze Laut ein Kommando wie „Stopp“? Antwortet mit dem
 * Stichwort oder `null` — auch bei Fehlern und nach `timeoutMs`, dann bleibt
 * es bei der Regel „eine Sekunde sprechen“.
 */
export async function pruefeStichwort(
  aufnahme: Blob,
  sprache: 'de' | 'en',
  timeoutMs = 1500,
): Promise<string | null> {
  const abbruch = new AbortController()
  const uhr = setTimeout(() => abbruch.abort(), timeoutMs)
  try {
    const res = await fetch(`/api/v1/voice/stichwort?sprache=${sprache}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'audio/wav' },
      body: aufnahme,
      signal: abbruch.signal,
    })
    const body = (await res.json().catch(() => ({}))) as { treffer?: string | null }
    return body.treffer ?? null
  } catch {
    return null
  } finally {
    clearTimeout(uhr)
  }
}

/** Die Live-Wand (`/wand`): Zählungen für den Bildschirm im Lab, ohne Sitzung. */
export interface WandStand {
  id: string
  slug: string
  title: string
  vorOrt: number
  heute: number
  erkanntZuletzt: number
  auslastung: 'frei' | 'ruhig' | 'gut besucht' | 'voll'
}
export interface Wand {
  stand: string
  besucherHeute: number
  fragenHeute: number
  imGespraech: number
  staende: WandStand[]
  frei: string[]
  beliebt: string[]
  letzteFragen: string[]
}
export const fetchWand = () => jsonFetch<Wand>('/api/v1/wand')

// --- Gewinnspiel: Bildschirm /gewinnspiel, Handy /gewinnspiel/go/<token> (routes/gewinnspiel.ts) ---

export type GewinnspielStand = GewinnspielZustand & { verbunden: boolean }
export type GewinnspielPreis = Pick<
  RafflePrize,
  'id' | 'company' | 'prize' | 'logoKey' | 'winnersCount' | 'participantCount'
>

export function fetchGewinnspielState() {
  return jsonFetch<GewinnspielStand>('/api/v1/gewinnspiel/state', { cache: 'no-store' })
}

export function fetchGewinnspielPreise() {
  return jsonFetch<{ preise: GewinnspielPreis[] }>('/api/v1/gewinnspiel/preise', {
    cache: 'no-store',
  })
}

/** Vom Bildschirm, jede Sekunde: Lebenszeichen und Stand in einem. */
export function reportGewinnspielScreen(phase: GewinnspielZustand['bildschirm']['phase']) {
  return jsonFetch<GewinnspielStand>('/api/v1/gewinnspiel/bildschirm', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ phase }),
    cache: 'no-store',
  })
}

/** Vom Handy: die Ziehung starten — ein Name je Preis, alle auf einmal. */
export function startGewinnspiel(token: string) {
  return jsonFetch<GewinnspielStand>(`/api/v1/gewinnspiel/start/${encodeURIComponent(token)}`, {
    method: 'POST',
  })
}

export function sendGewinnspielBefehl(
  token: string,
  befehl: Exclude<GewinnspielBefehl, { art: 'start' }>,
) {
  return jsonFetch<GewinnspielStand>(`/api/v1/gewinnspiel/befehl/${encodeURIComponent(token)}`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(befehl),
  })
}

export type GewinnspielAnsage = 'start' | 'gewinner' | 'neu' | 'fertig'

/** K4ias Ansage als Tonspur, für ein Audio-Element am Bildschirm. */
export function gewinnspielSpeechUrl(art: GewinnspielAnsage): string {
  return `/api/v1/gewinnspiel/speech?art=${art}`
}

// --- Gemeinsame Fernbedienung: Bildschirm /buehne, Handy /buehne/go/<token> (routes/buehne.ts) ---

export type BuehnenModus = 'intro' | 'gespraech' | 'gewinnspiel' | 'praesentation'

/** Was der große Bildschirm zeigen soll; `nr` steigt mit jedem Wechsel. */
export function fetchBuehneModus() {
  return jsonFetch<{ modus: BuehnenModus; nr: number }>('/api/v1/buehne/modus', {
    cache: 'no-store',
  })
}

export function setBuehneModus(token: string, modus: BuehnenModus) {
  return jsonFetch<{ modus: BuehnenModus; nr: number }>(
    `/api/v1/buehne/modus/${encodeURIComponent(token)}`,
    {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ modus }),
    },
  )
}

/** Die Tokens von Intro und Gewinnspiel — mit ihnen spricht das Handy deren Routen an. */
export function fetchBuehneZugang(token: string) {
  return jsonFetch<{ introToken: string; gewinnspielToken: string }>(
    `/api/v1/buehne/zugang/${encodeURIComponent(token)}`,
    { cache: 'no-store' },
  )
}

// --- Präsentation auf der Bühne (routes/praesentation.ts) ---

export type PraesentationPhase = 'bereit' | 'laeuft' | 'fragen' | 'fertig'
export type PraesentationBefehl =
  | {
      art:
        | 'start'
        | 'weiter'
        | 'zurueck'
        | 'pause'
        | 'fortsetzen'
        | 'quartale'
        | 'kurzfassung'
        | 'fragen'
        | 'ende'
        | 'automatik_an'
        | 'automatik_aus'
    }
  | { art: 'springen'; folie: number }

/** Der Stand der Präsentation, wie Bildschirm und Handy ihn jede Sekunde lesen. */
export interface PraesentationStand {
  phase: PraesentationPhase
  folie: number
  /** Auf der ersten Quartalsfolie läuft die Kurzfassung statt des Folientexts. */
  kurzfassung: boolean
  /** Erste Quartalsfolie steht; das Handy muss wählen, der Bildschirm spricht noch nicht. */
  entscheidung: boolean
  /** Steigt, wenn der Bildschirm etwas Neues sprechen soll. */
  runde: number
  befehl: { art: 'pause' | 'fortsetzen' | 'keiner'; nr: number }
  chatSitzung: string | null
  /** K4ia klickt nach jeder Folie selbst weiter (bis zur Entscheidung und vor der Fragerunde). */
  automatik: boolean
  bildschirm: { folie: number; spricht: boolean; pausiert: boolean; gemeldetAm: number }
  verbunden: boolean
}

export interface PraesentationFolie {
  nr: number
  titel: string
  bild: boolean
  /** Nur am Handy. */
  text?: string
}

export interface PraesentationFolien {
  folien: PraesentationFolie[]
  quartaleVon: number | null
  quartaleBis: number | null
}

export function fetchPraesentationStand() {
  return jsonFetch<PraesentationStand>('/api/v1/praesentation/state', { cache: 'no-store' })
}

const mitK = (pfad: string, k: string) =>
  `${pfad}${pfad.includes('?') ? '&' : '?'}k=${encodeURIComponent(k)}`

/** Vom Bildschirm (`k` aus der Adresse im Admin): die Folien ohne Texte, dazu die Pausen der Automatik. */
export function fetchPraesentationFolien(k: string) {
  return jsonFetch<
    PraesentationFolien & {
      kurzfassung: boolean
      autoPauseSekunden: number
      autoStummSekunden: number
    }
  >(mitK('/api/v1/praesentation/folien', k), { cache: 'no-store' })
}

/** Vom Handy: die Folien mit Sprechtexten und die Kurzfassung zum Mitlesen. */
export function fetchPraesentationFolienHandy(token: string) {
  return jsonFetch<PraesentationFolien & { kurzfassung: string }>(
    `/api/v1/praesentation/handy/${encodeURIComponent(token)}/folien`,
    { cache: 'no-store' },
  )
}

/** Das Bild einer Folie — Bildschirm mit `k`, Handy mit `t`. */
export function praesentationBildUrl(nr: number, zugang: { k: string } | { t: string }): string {
  const pfad = `/api/v1/praesentation/folie/${nr}/bild`
  return 'k' in zugang ? mitK(pfad, zugang.k) : `${pfad}?t=${encodeURIComponent(zugang.t)}`
}

/** Die Tonspur: der Text der Folie oder, auf der ersten Quartalsfolie, die Kurzfassung. */
export function praesentationTonUrl(quelle: { folie: number } | 'kurzfassung', k: string): string {
  return mitK(
    quelle === 'kurzfassung'
      ? '/api/v1/praesentation/kurzfassung/ton'
      : `/api/v1/praesentation/folie/${quelle.folie}/ton`,
    k,
  )
}

/** Vom Bildschirm: Tonspur zu Ende, Pause vorbei — der Worker klickt weiter, wenn die Automatik an ist. */
export function sendPraesentationAutoWeiter(k: string) {
  return jsonFetch<PraesentationStand>(mitK('/api/v1/praesentation/auto-weiter', k), {
    method: 'POST',
  })
}

export function sendPraesentationBefehl(token: string, befehl: PraesentationBefehl) {
  return jsonFetch<PraesentationStand>(
    `/api/v1/praesentation/befehl/${encodeURIComponent(token)}`,
    {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(befehl),
    },
  )
}

/** Vom Bildschirm, jede Sekunde. */
export function reportPraesentationBildschirm(
  k: string,
  meldung: { folie: number; spricht: boolean; pausiert: boolean },
) {
  return jsonFetch<PraesentationStand>(mitK('/api/v1/praesentation/bildschirm', k), {
    method: 'POST',
    cache: 'no-store',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(meldung),
  })
}

/** Vom Handy: eine Frage aus dem Publikum, erst in der Fragerunde. Antwort als Stream (lib/sse.ts). */
export function sendPraesentationFrage(token: string, message: string, signal?: AbortSignal) {
  return fetch(`/api/v1/praesentation/frage/${encodeURIComponent(token)}`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ message }),
    signal,
  })
}

export interface PraesentationNachricht {
  id: string
  role: 'user' | 'assistant'
  content: string
}

export function fetchPraesentationVerlauf(k: string) {
  return jsonFetch<{ nachrichten: PraesentationNachricht[] }>(
    mitK('/api/v1/praesentation/verlauf', k),
    { cache: 'no-store' },
  )
}

export function fetchPraesentationVerlaufHandy(token: string) {
  return jsonFetch<{ nachrichten: PraesentationNachricht[] }>(
    `/api/v1/praesentation/handy/${encodeURIComponent(token)}/verlauf`,
    { cache: 'no-store' },
  )
}

/** Vom Bildschirm: eine Antwort der Fragerunde als Tonspur. */
export function praesentationAntwortTonUrl(nachricht: string, k: string): string {
  return mitK(`/api/v1/praesentation/antwort/ton?nachricht=${encodeURIComponent(nachricht)}`, k)
}

// --- Ansage (routes/ansage.ts): ein Text aus dem Admin, den K4ia beim Antippen spricht ---

export interface AnsageTeam {
  name: string
  /** Pfad über /media oder null. */
  logo: string | null
}

export interface Ansage {
  title: string
  text: string
  sprachausgabe: boolean
  /** Auslosung nach der Ansage — null, wenn keine eingerichtet ist. */
  tipp: {
    teamA: AnsageTeam
    teamB: AnsageTeam
    gewinner: 'a' | 'b' | 'unentschieden'
    ergebnis: string
    /** Was K4ia danach sagt; leer = nichts. */
    text: string
  } | null
}

export function fetchAnsage(id: string) {
  return jsonFetch<Ansage>(`/api/v1/ansage/${encodeURIComponent(id)}`, { cache: 'no-store' })
}

export function ansageTonUrl(id: string, was: 'text' | 'tipp' = 'text'): string {
  return `/api/v1/ansage/${encodeURIComponent(id)}/${was === 'tipp' ? 'tipp/ton' : 'ton'}`
}
